// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGaugeContent.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartGaugeContent model
 */

import { extendObservable, computed, observable, reaction, toJS, action } from 'mobx';
import ReportPartContent from './Izenda.ReportPartContent';
import {
  placeholder,
  get,
  compact,
  size,
  concat,
  flatMap,
  omit,
  assign,
  isEmpty,
  getOr,
  isFunction,
  invoke,
  some,
  includes,
  find,
  findIndex,
  flow,
  filter,
  isUndefined
} from 'lodash/fp';

import tracker from 'share/tracking/Izenda.Tracker';
import {
  populateReportChartContainer,
  getElements,
  hasElement,
  addContainers,
  getDefaultFunction,
  checkCompatibleFunctionCommon
} from 'share/utils/Izenda.ReportPartUtils';
import { EMPTY_FUNCTION, SORT_ORDERS } from 'logic/data/Izenda.ReportPartFieldConstants';
import { EXPANDED_LEVEL, REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import CONSTANTS, { ContainerName } from 'logic/data/Izenda.ReportPartGaugeConstants';
import { AVAILABLE_FUNCTION_CONFIG } from 'logic/data/Izenda.GaugeAvailableFunction';
import GaugeValueDetailsModel from './Izenda.ReportPartGaugeValueDetails';
import { exportClass } from 'share/Izenda.ServiceProvider';

const NUMBER_ZERO = 0;
const NUMBER_ONE = 1;
const NUMBER_CHECK_INDEX = -1;

@exportClass('ReportPartGaugeContent')
@tracker
export default class ReportPartGaugeContent extends ReportPartContent {
  @observable isCrossFiltering = false;
  // @observable type = null;
  @observable title = null;
  @observable description = null;
  @observable
  properties = {
    chartType: CONSTANTS.DefaultChartType,
    colorTheme: undefined,
    isDefaultTheme: true,
    optionByType: {
      izUseSeparator: true,
      izUsePagination: true,
      izItemPerRow: CONSTANTS.ITEM_PER_ROWS
    },
    view: {
      showLabels: false,
      dataRefreshInterval: {
        enable: false,
        updateInterval: 0,
        isAll: true,
        latestRecord: 0
      }
    },
    printing: {
      izPageBreakAfterSeparator: false
    }
  };
  @observable
  drilldownInfo = {
    separatorValue: null,
    filters: []
  };
  // use to show/hide fields which need to draw chart - do not save
  @observable activeSerie = null;

  constructor(reportPartContent) {
    super(REPORT_PART_TYPE.Gauge);

    this.staticProperties = {};
    const props = {};
    // important - these containers save list of fields to get data and render report
    addContainers(props, ContainerName.LABELS);
    addContainers(props, ContainerName.VALUES);
    addContainers(props, ContainerName.SEPARATORS);

    extendObservable(this, props);

    reportPartContent && this.readData(reportPartContent);

    this.drilldownReaction = reaction(() => {
      // trick - access num elements of each container to trigger change
      // every have action on component (add/delete field, visible/hidden element)
      return (
        this[ContainerName.LABELS].numElement +
        this[ContainerName.VALUES].numElement +
        this[ContainerName.SEPARATORS].numElement
      );
    }, this.resetDrilldownInfo.bind(this));

    this.cleanDirty();
  }

  @computed
  get chartType() {
    return this.properties.chartType;
  }

  @computed
  get expandedLevel() {
    return this.drilldownInfo.filters.length;
  }

  @computed
  get drilldown() {
    return this.getDrilldownInfo();
  }

  @computed
  get serieChartList() {
    const valuesElements = this.values.visibleElement || [];
    return valuesElements.map(elm => elm);
  }

  @computed
  get activeElmsByContainers() {
    const activeSerieKey = get('fieldName', this.activeSerie);

    return {
      labels: getElements.call(this, this.labels),
      values: getElements.call(this, this.values, activeSerieKey),
      separators: getElements.call(this, this.separators)
    };
  }

  @computed
  get containerCollection() {
    const basicContainers = compact([this.labels, this.values, this.separators]);

    // element for thresholds & sup-KPI (simple gauge only)
    let addedContainer = {};
    addContainers(addedContainer, ContainerName.THRESHOLD);
    addContainers(addedContainer, ContainerName.SUP_KPI);

    const thresholdsElm = addedContainer[ContainerName.THRESHOLD].elements;
    const supKpiElm = addedContainer[ContainerName.SUP_KPI].elements;

    this.values.visibleElement.forEach(element => {
      const metric = get('properties.metric', element);
      const supKPI = get('properties.metric.supplementaryKPI.element', element);

      if (size(metric.thresholds.levels)) {
        metric.thresholds.levels.forEach(level => {
          level.element && thresholdsElm.push(level.element);
        });
      }

      supKPI && supKpiElm.push(supKPI);
    });

    addedContainer = observable(addedContainer);

    return concat(basicContainers, [addedContainer[ContainerName.THRESHOLD], addedContainer[ContainerName.SUP_KPI]]);
  }

  @computed
  get isApplyScale() {
    return CHART_TYPE.SimpleGauge !== this.chartType;
  }

  @computed
  get hasDrillDown() {
    return get('numElement', this.labels) > NUMBER_ONE;
  }

  @computed
  get isBeingBuild() {
    return hasElement(this.values);
  }

  @computed
  get useSeparator() {
    return get('optionByType.izUseSeparator', this.properties || {});
  }

  @computed
  get dataRefreshInterval() {
    return get('view.dataRefreshInterval', this.properties);
  }

  @computed
  get optionalParams() {
    return {
      expandedLevel: EXPANDED_LEVEL[this.hasDrillDown ? 'FIRST_LEVEL' : 'ALL']
    };
  }

  @computed
  get reportPartStyle() {
    return this.properties.chartType;
  }

  @computed
  get labelsElements() {
    return this.labels.visibleElement;
  }

  @computed
  get valuesElements() {
    return this.values.visibleElement;
  }

  @computed
  get allFieldCollection() {
    return flatMap(item => (item.elements ? item.elements.slice() : []), this.containerCollection);
  }

  @computed
  get isShowMetric() {
    return !isEmpty(this.serieChartList) && this.serieChartList.length > NUMBER_ONE;
  }

  /**
   * Determine whether the report part content automatically sizes its container or not
   */
  get autoSize() {
    return false;
  }

  /**
   * validate function based on container
   * @method validateFunction
   * @param {Object} container Container need validateFunction
   * @param {Function} functionInfo Function need validate
   * @return {Object}
   */
  validateFunction(container: any, functionInfo: () => mixed): any {
    if (!functionInfo) {
      return {
        status: false,
        useDefaultFunction: false
      };
    }

    return {
      status: true
    };
  }

  /**
   * Get Default Function And Format for field in container
   * @method getDefaultFunctionFormat
   * @param {Object} container Container have element need get default dataType
   * @param {string} dataType  Data Type for field
   * @return {Object}
   */
  getDefaultFunctionFormat(container: any, dataType: string): any {
    const byContainer = CONSTANTS.DefaultFunction[this.chartType][container.name || ''] || {};
    return getDefaultFunction(byContainer, dataType);
  }

  /**
   * read data from api/create report part to update data
   * @param {Object} content [data from api/create report part]
   */
  @action
  readData(content: any): void {
    content = content || {};
    const properties = content.properties || {};
    this.isCrossFiltering = content.isCrossFiltering;
    // this.type = content.type;
    this.labels = populateReportChartContainer(content.labels, 'labels');
    this.separators = populateReportChartContainer(content.separators, 'separators');
    this.values = populateReportChartContainer(content.values, 'values', this.afterInsertFieldToContainer);

    this.settings = content.settings;

    // report part properties
    this.setChartType(properties.chartType);

    // report part properties
    this.staticProperties = get('staticProperties', properties);
    this.setReportPartProperties(omit('staticProperties', properties));
    this.setActiveSerie(null, find({ fieldName: content.selectedMetric }, this.serieChartList));

    this.markDirty();
  }

  /**
   * Build data for save
   * @return {Object}
   */
  @action
  buildDataForSave(): any {
    const chartContent = toJS(omit(['properties', 'staticProperties', 'expandedLevel', 'drilldownInfo'], this));

    // merge static properties
    chartContent.properties = this.getReportPartProperties();
    chartContent.selectedMetric = get('activeSerie.fieldName', this);

    return chartContent;
  }

  /**
   * Set data by data key
   * @param {string} key
   * @param {Object} newVal
   */
  setDataByKey(key: string, newVal: any): void {
    this[key] = newVal;
    this.markDirty();
  }

  /**
   * Get data by data key
   * @param {string} key
   * @return {Object}
   */
  getDataByKey(key): any {
    return this[key];
  }

  /**
   * Clean dirty status.
   */
  cleanDirty(): void {
    this.resetDirty();
  }

  /**
   * get properties of report part, if propKey is null, return all properties
   * @param {string} propKey
   * @return {Object}
   */
  getReportPartProperties(propKey: string): any {
    const props = toJS(this.properties) || {};

    if (!propKey) {
      return assign({ staticProperties: this.staticProperties }, props);
    }

    return (
      get(propKey, props.commonOptions || {}) ||
      get(propKey, props.optionByType || {}) ||
      get(propKey, props.view || {}) ||
      get(propKey, props.printing || {}) ||
      get(propKey, this.defaultCommonOptions || {}) ||
      get(propKey, this.defaultOptionsByType || {})
    );
  }

  /**
   * Get metric container
   */
  getMetricContainer() {
    return ContainerName.VALUES;
  }

  /**
   * set properties of report part
   * @param {Object} newProps
   */
  @action
  setReportPartProperties(newProps: any): void {
    const props = toJS(this.properties) || {};
    newProps = toJS(newProps);
    extendObservable(props, newProps);
    this.properties = props;
    if (!isUndefined(newProps.colorTheme) || newProps.isDefaultTheme) {
      props.isDefaultTheme = newProps.isDefaultTheme;
      props.colorTheme = newProps.colorTheme;
    }

    this.markDirty();
  }

  /**
   * Get dirty status.
   * @returns {boolean}
   */
  checkDirty(): boolean {
    const dirtyElements = flow(
      flatMap(item => (item.elements ? item.elements.peek() : [])),
      filter(element => {
        const metric = get('properties.metric', element);
        return (element.checkDirty && element.checkDirty()) || (metric && metric.checkDirty && metric.checkDirty());
      })
    )(this.containerCollection);

    const properties = this.properties || {};
    const metric = properties.metric;

    return (
      this.isDirty ||
      !isEmpty(dirtyElements) ||
      (properties.checkDirty && properties.checkDirty()) ||
      (metric && metric.checkDirty && metric.checkDirty())
    );
  }

  /**
   * Set Chart type
   * @param {string} nextType
   */
  @action
  setChartType(nextType: string): void {
    const previousType = this.properties.chartType;
    this.properties.chartType = nextType;
    this.convertChartType(previousType, nextType);

    this.markDirty();
  }

  /**
   * Convert chart type
   * @param {string} previousType
   * @param {string} nextType
   */
  @action
  convertChartType(previousType: string, nextType: string): void {
    switch (nextType) {
      case CHART_TYPE.SolidGauge:
      case CHART_TYPE.LinearGauge:
        initValuesData.call(this, 'resetSupplementaryKPISetting');
        break;

      case CHART_TYPE.SimpleGauge:
        initValuesData.call(this, 'resetScaleData');
        break;
      default:
      // do nothing
    }

    this.markDirty();

    function initValuesData(fnName) {
      let fieldMetricVal;

      this.values.elements.map(field => {
        fieldMetricVal = getOr({}, 'properties.metric', field);
        isFunction(fieldMetricVal[fnName]) && fieldMetricVal[fnName]();
        invoke('cleanDirty', fieldMetricVal);
      });
    }
  }

  /**
   * Set active serie key, used to show/hide serie chart in metric dropdown
   * @param {string} activeSerieKey
   * @param {Object} activeSerieInfo
   */
  setActiveSerie(activeSerieKey: string, activeSerieInfo: any): void {
    this.activeSerie = activeSerieInfo;
    this.markDirty();
  }

  /**
   * reset elements of separtors
   */
  resetSeparator(): void {
    this.separators.elements.clear();
  }

  /**
   * If element is inserted to values container, need to init a model
   * to manage its information: scale, threshold, supplementary KPI
   * @param {Object} container
   * @param {Object} newElement
   * @param {Object} rawData
   */
  @action
  afterInsertFieldToContainer(container: any, newElement: any, rawData: any): void {
    if (container.name === ContainerName.VALUES) {
      newElement.properties.metric = new GaugeValueDetailsModel(getOr({}, 'properties.metric', rawData));
    }
  }

  /**
   * After add field
   */
  @action
  afterAddField(): void {
    // Set sort value 'ASC' for first field in LABEL if there isn't any existing sorted fields
    const firstPosition = 1;
    const lengthToApplySort = 2;
    const { ASC, DESC } = SORT_ORDERS;
    const needSortFirstField =
      this.labelsElements.length >= lengthToApplySort &&
      !some(field => {
        const sort = get('properties.dataFormattings.sort', field);
        return includes(sort, [ASC.value, DESC.value]);
      }, this.allFieldCollection);
    const firstField = find(ele => ele.position === firstPosition, this.labelsElements);
    this.isBeingBuild && needSortFirstField && firstField.properties.setSortValue(ASC.value);
  }

  /**
   * Reset drilldown info
   */
  @action
  resetDrilldownInfo(): void {
    this.drilldownInfo = {
      filters: [],
      separatorValue: null
    };
  }

  /**
   * Update drilldown inform
   * @param {Object} drilldownValue
   * @param {Object} separatorValue
   * @return {boolean}
   */
  @action
  updateDrilldownInfo(drilldownValue: any, separatorValue: any): boolean {
    if (!this.hasDrillDown) {
      return false;
    }

    const drilldownInfo = this.drilldownInfo;
    const filters = get('filters', drilldownInfo);
    let canDrilldown = false;

    // move to top level
    if (!drilldownValue && size(filters)) {
      this.resetDrilldownInfo();
      canDrilldown = true;
      return canDrilldown;
    }

    const nextDrilldownKey = get('field.fieldNameAlias', drilldownValue) || get('key', drilldownValue);
    const drillUpLevel = findIndex({ key: nextDrilldownKey }, filters);
    const columnName = get('field.columnName', drilldownValue);

    if (drillUpLevel > NUMBER_CHECK_INDEX) {
      // drill up
      filters.splice(drillUpLevel + NUMBER_ONE, filters.length - drillUpLevel - NUMBER_ONE);
      canDrilldown = true;
    } else if (filters.length < this[ContainerName.LABELS].numElement - NUMBER_ONE) {
      // drill down
      canDrilldown = true;

      filters.push({
        key: nextDrilldownKey,
        value: get(columnName, drilldownValue.record),
        field: get('field', drilldownValue),
        record: get('record', drilldownValue)
      });

      separatorValue &&
        separatorValue.field &&
        (drilldownInfo.separatorValue = {
          key: get('field.fieldNameAlias', separatorValue),
          value: get('value', separatorValue),
          field: get('field', separatorValue)
        });
    }

    return canDrilldown;
  }

  /**
   * Return drilldown info: expandedLevel & filters
   * @return {Object}
   */
  getDrilldownInfo(): any {
    const drilldownInfo = this.drilldownInfo;
    const expandedLevel = this.expandedLevel;

    let filters = drilldownInfo.filters.map(filter => {
      return {
        key: get('key', filter),
        value: get('value', filter),
        dataType: get('field.reportPartElm.fieldDataType', filter)
      };
    });

    const separatorInfo = {
      key: get('key', drilldownInfo.separatorValue),
      value: get('value', drilldownInfo.separatorValue),
      isSeparator: true
    };

    filters =
      expandedLevel < NUMBER_ZERO
        ? []
        : drilldownInfo.separatorValue
        ? filters.unshift(separatorInfo) && filters
        : filters;

    return {
      expandedLevel: expandedLevel,
      filters: filters
    };
  }

  /**
   * Get cross filtering info.
   * @return {Array<any>} filter info
   */
  getCrossFilteringInfo(): Array<any> {
    return this.drilldownInfo.filters.map(filter => {
      return {
        key: get('key', filter),
        value: get('value', filter),
        field: get('field', filter)
      };
    });
  }

  /**
   * This properties is used to save the page size
   * Only use for chart-type which supported pagination: pie, donut, treemap, funnel
   * @param {number} pageSize
   */
  setPageSize(pageSize: number): void {
    this.staticProperties.pageSize = pageSize;
  }

  /**
   * Check need to break page when printing.
   * @returns {boolean}
   */
  checkBreakPage(): boolean {
    return (
      this.properties.printing.izPageBreakAfterSeparator &&
      this.separators.elements &&
      this.separators.elements.length > NUMBER_ZERO
    );
  }

  /**
   * Reset drilldown info whenever reorder field
   */
  @action
  changePositionField(): void {
    this.resetDrilldownInfo();
  }

  /**
   * Action whenever element's props are changed.
   * @param {Object} element
   * @param {Object} elementProps
   * @param {string} changedKey
   */
  changePropertiesByField(element: any, elementProps: any, changedKey: string): void {
    if (changedKey === 'function' || changedKey === 'format') {
      this.resetDrilldownInfo();
    }
  }

  /**
   * check if function is compatible with element
   * @method checkedCompatibleFunction
   * @param {Object} container Container that element belong
   * @param {Object} element Element need to validate
   * @param {Function} functionInfo Function infomation
   * @return {boolean}
   */
  checkedCompatibleFunction(container: any, element: any, functionInfo: () => mixed): boolean {
    if (!container) {
      return true;
    }

    let result = true;
    const functionName = getOr(EMPTY_FUNCTION.name, 'name', functionInfo);

    // If the user add a calculated field which contains the aggregated field
    // then the user cannot select any aggregated field in Function dropdown in
    // Field Properties Panel. The user can select “empty” function or other user
    // defined functions which has function level = field.
    if (element.field.hasAggregatedFunction) {
      result = functionInfo.userDefined || !functionName;
    }

    const checkDataTypeNumericMoney = includes(placeholder, ['Numeric', 'Money']);

    if (result && [ContainerName.VALUES, ContainerName.THRESHOLD].indexOf(container.name) > NUMBER_CHECK_INDEX) {
      // function return Money or Numeric
      result =
        checkDataTypeNumericMoney(functionInfo.formatDataType) ||
        // Element data type is Money or numeric and function is empty function
        (checkDataTypeNumericMoney(element.fieldDataType) && !functionName);
    }

    let config = AVAILABLE_FUNCTION_CONFIG[this.chartType];

    config = config && config[container.name];

    if (!config || !result) {
      return result;
    }

    return checkCompatibleFunctionCommon(functionInfo, element, config);
  }

  /**
   * Check if this field can have state Unsort
   * @param {Object} container
   * @returns {boolean}
   */
  checkCanUnsort(container: any): boolean {
    const containerName = getOr('', 'name', container);
    let canUnsort = false;

    switch (this.chartType) {
      case CHART_TYPE.SolidGauge:
      case CHART_TYPE.LinearGauge:
      case CHART_TYPE.SimpleGauge:
        canUnsort = containerName !== ContainerName.SEPARATORS && containerName !== ContainerName.THRESHOLD;
        break;
    }

    return canUnsort;
  }

  /**
   * Reset refresh interval setting to default vale
   * @return {Object} default value
   */
  @action
  resetRefreshIntervalValue(): any {
    this.properties.view.dataRefreshInterval = CONSTANTS.DEFAULT_SETTING_VALUE.refreshIntervalDefault;
    this.markDirty();
    return this.properties.view.dataRefreshInterval;
  }

  /**
   * Reset border setting to default value
   * @return {Object} default value
   */
  @action
  resetBorderDefaultValue(): any {
    this.properties.commonOptions = CONSTANTS.DEFAULT_SETTING_VALUE.borderDefault;
    this.markDirty();
    return this.properties.commonOptions;
  }

  /**
   * Check if ReportPart is drilling down or not
   * @return {boolean}
   */
  isDrillingDown(): boolean {
    return !flow(
      get('drilldownInfo.filters'),
      isEmpty
    )(this);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartGaugeContent.js